import { defaultFor, type FeatureFlagKey } from '@starter/api/flags';
import posthog from 'posthog-js';
import { ApiError } from '../lib/api.ts';

/**
 * Browser analytics and error reporting.
 *
 * The key is fetched rather than compiled in. The web bundle is built once and
 * the same image is promoted from a preview stand to production, so a
 * build-time VITE_POSTHOG_KEY would either bake the wrong environment into
 * every stand or force a rebuild per deploy — giving up the property that
 * apps/web/vite.config.ts exists to protect. /api/client-config is the same
 * answer the sign-in screen already uses for social providers.
 *
 * The cost is that reporting starts one round-trip late, which is what the
 * buffer below is for.
 */

interface PostHogConfig {
  key: string;
  host: string;
  environment: string;
  release: string;
  /** Flags already evaluated for a signed-in visitor; null when anonymous. */
  bootstrap: { distinctId: string; flags: Record<string, boolean | string> } | null;
}

let ready = false;

/**
 * An identity established before the key arrived. Signing in resolves faster
 * than a config fetch often enough to matter, and without this the user would
 * stay anonymous for the whole session: the effect that calls identifyUser()
 * only re-runs when the session itself changes, which it no longer will.
 */
let pendingIdentity: { id: string; email: string } | undefined;

/**
 * Components watching flag values. PostHog's own onFeatureFlags only exists
 * after init, and the interesting transitions here happen *around* init — the
 * bundle renders, the key arrives, flags load. One list, notified on all three,
 * keeps useFeatureFlag from having to know which of them it is waiting for.
 */
const flagListeners = new Set<() => void>();

function notifyFlagListeners(): void {
  for (const listener of flagListeners) listener();
}

/**
 * Errors thrown before the config arrived. Module evaluation, the first render
 * and any failed preload all happen inside that window, and those are the
 * failures worth having most — they are the ones a user sees as a blank page.
 */
const pending: unknown[] = [];
const PENDING_LIMIT = 20;

/** Extra context PostHog cannot infer, most importantly the API's request id. */
function describe(error: unknown): Record<string, unknown> {
  if (error instanceof ApiError) {
    return {
      request_id: error.requestId,
      $request_id: error.requestId,
      'http.status': error.status,
    };
  }
  return {};
}

/**
 * Reports an error. Safe to call before (and without) initialisation: early
 * errors are buffered and flushed once the key arrives, and with nothing
 * configured nothing leaves the browser.
 */
export function reportError(error: unknown): void {
  if (!ready) {
    // A render loop can throw thousands of times a second. Keeping the first
    // few diagnoses it and bounds the memory a broken page can take.
    if (pending.length < PENDING_LIMIT) pending.push(error);
    return;
  }

  posthog.captureException(error, describe(error));
}

function onWindowError(event: ErrorEvent): void {
  reportError(event.error ?? event.message);
}

function onRejection(event: PromiseRejectionEvent): void {
  reportError(event.reason);
}

/**
 * Starts analytics and error reporting. Call it first thing, before rendering:
 * the listeners are attached synchronously so nothing is missed, while the
 * configuration fetch runs in the background and never blocks the first paint.
 */
export function startAnalytics(): void {
  window.addEventListener('error', onWindowError);
  window.addEventListener('unhandledrejection', onRejection);

  void configure();
}

async function configure(): Promise<void> {
  let config: PostHogConfig | null = null;

  try {
    const response = await fetch('/api/client-config', { credentials: 'same-origin' });
    if (!response.ok) return;
    config = ((await response.json()) as { posthog: PostHogConfig | null }).posthog;
  } catch {
    // Offline, or the API is down — in which case the user has a much louder
    // problem than a missing analytics event.
    return;
  }

  if (!config) {
    // Nothing configured for this environment. Stop buffering; the listeners
    // stay attached but reportError becomes a no-op that drops on the floor.
    pending.length = 0;
    return;
  }

  posthog.init(config.key, {
    api_host: config.host,
    // TanStack Router navigates through the history API and never reloads the
    // document, so the default load-event pageview would record exactly one
    // pageview per session. This is the SPA-aware mode.
    capture_pageview: 'history_change',
    capture_pageleave: 'if_capture_pageview',
    // Unhandled errors and rejections, captured by the SDK from here on — the
    // listeners above are removed below so nothing is reported twice.
    capture_exceptions: true,
    // Anonymous visitors are counted without creating a person profile each;
    // a profile appears when someone signs in and identify() runs.
    person_profiles: 'identified_only',
    // Values the API already evaluated for this user, so the first render
    // branches correctly instead of flashing the default and correcting itself
    // a round-trip later. PostHog replaces them with fresh values on load.
    ...(config.bootstrap
      ? {
          bootstrap: {
            distinctID: config.bootstrap.distinctId,
            isIdentifiedID: true,
            featureFlags: config.bootstrap.flags,
          },
        }
      : {}),
  });

  // Every event carries which stand it came from and which commit was serving,
  // which is what makes a preview's events separable from production's.
  posthog.register({ environment: config.environment, release: config.release });

  window.removeEventListener('error', onWindowError);
  window.removeEventListener('unhandledrejection', onRejection);
  ready = true;

  // Fires when flags first load and whenever they are refreshed.
  posthog.onFeatureFlags(() => notifyFlagListeners());
  // And once now, for anything that rendered against defaults before init.
  notifyFlagListeners();

  for (const error of pending.splice(0)) reportError(error);

  if (pendingIdentity) {
    const { id, email } = pendingIdentity;
    pendingIdentity = undefined;
    posthog.identify(id, { email });
  }
}

/**
 * Ties subsequent events to a person. Called when a session appears; PostHog
 * stitches the preceding anonymous events onto the same person.
 */
export function identifyUser(id: string, email: string): void {
  if (!ready) {
    pendingIdentity = { id, email };
    return;
  }
  posthog.identify(id, { email });
}

/**
 * Ends the identified session on sign-out. Without it the next person to use
 * the same browser inherits the previous one's identity.
 */
export function resetUser(): void {
  pendingIdentity = undefined;
  if (!ready) return;
  posthog.reset();
}

/**
 * Subscribes to flag changes. Paired with flagEnabled() below, this is the
 * external store that useFeatureFlag() reads through useSyncExternalStore.
 */
export function subscribeToFlags(listener: () => void): () => void {
  flagListeners.add(listener);
  return () => flagListeners.delete(listener);
}

/**
 * A flag's current value, falling back to the default declared in
 * `@starter/api/flags` — before init, with no project configured, and for a
 * flag PostHog does not return.
 */
export function flagEnabled(key: FeatureFlagKey): boolean {
  if (!ready) return defaultFor(key);
  return posthog.isFeatureEnabled(key) ?? defaultFor(key);
}

/** The variant of a multivariate flag, or undefined when there is no answer. */
export function flagVariant(key: FeatureFlagKey): string | boolean | undefined {
  if (!ready) return undefined;
  return posthog.getFeatureFlag(key);
}
